import { useState } from "react";
import { toast } from "sonner";
import { Zap, Link2, Lightbulb, ArrowRight } from "lucide-react";
import { api, errDetail } from "@agent/lib/api";
import { inputCls, selectCls, labelCls } from "@agent/lib/styles";

const TABS = [
  { id: "topic", label: "Topic", icon: Zap },
  { id: "url", label: "URL / Paper", icon: Link2 },
  { id: "suggest", label: "Suggest", icon: Lightbulb },
];

export function DraftInputForm({ generating, onGenerate }) {
  const [tab, setTab] = useState("topic");
  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");
  const [url, setUrl] = useState("");
  const [tone, setTone] = useState("technical");
  const [length, setLength] = useState("medium");
  const [format, setFormat] = useState("post");
  const [focus, setFocus] = useState("");
  const [topics, setTopics] = useState([]);
  const [suggesting, setSuggesting] = useState(false);

  const generate = () => {
    const payload = { mode: tab === "url" ? "url" : "topic", topic, notes, url, tone, length, format };
    if (payload.mode === "topic" && !topic.trim()) { toast.error("Enter a topic first"); return; }
    if (payload.mode === "url" && !url.trim()) { toast.error("Enter a URL first"); return; }
    onGenerate(payload);
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post("/suggest-topics", { focus });
      setTopics(data.topics || []);
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setSuggesting(false);
    }
  };

  const pickSuggestion = (t) => { setTopic(t.title); setNotes(t.angle || ""); setTab("topic"); };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-[0_4px_30px_rgba(15,23,42,0.05)] p-6 space-y-6 fade-up">
      <div>
        <span className="inline-block px-3 py-1 rounded-full border border-slate-200 text-[11px] tracking-[0.15em] uppercase text-slate-500 font-semibold mb-3">Input mode</span>
        <h1 className="font-display font-bold tracking-tight text-2xl text-[#0f172a]">
          Draft posts that <span className="grad-text">ship themselves.</span>
        </h1>
      </div>
      <div className="flex bg-slate-100 rounded-full p-1">
        {TABS.map(({ id: t, label, icon: Icon }) => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-full text-xs font-semibold transition-colors duration-150 ${
              tab === t ? "bg-[#0f172a] text-white shadow" : "text-slate-500 hover:text-[#0f172a]"}`}>
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {tab === "suggest" && (
        <div className="space-y-3 fade-up">
          <input data-testid="suggest-focus-input" className={inputCls} value={focus} onChange={(e) => setFocus(e.target.value)}
            placeholder="Focus area, e.g. Rust, LLM infra, Kubernetes… (optional)" />
          <button data-testid="suggest-topics-btn" onClick={suggest} disabled={suggesting}
            className="w-full py-3 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 text-white text-sm font-semibold hover:opacity-90 transition-opacity duration-150 disabled:opacity-40 flex items-center justify-center gap-2">
            <Lightbulb size={15} /> {suggesting ? "Thinking…" : "Suggest 5 topics"}
          </button>
          <div className="space-y-2">
            {topics.map((t, i) => (
              <button key={t.title || i} data-testid={`topic-suggestion-${i}`} onClick={() => pickSuggestion(t)}
                className="w-full text-left bg-slate-50 border border-slate-200 rounded-xl p-4 hover:border-indigo-300 hover:bg-indigo-50/40 hover:-translate-y-[1px] transition-all duration-150">
                <p className="text-sm text-[#0f172a] font-semibold">{t.title}</p>
                <p className="text-xs text-slate-500 mt-1">{t.angle}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {tab === "topic" && (
        <div className="space-y-4 fade-up">
          <div>
            <label className={labelCls}>Topic</label>
            <input data-testid="topic-input" className={inputCls} value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Why we moved from REST to gRPC" />
          </div>
          <div>
            <label className={labelCls}>Rough notes (optional)</label>
            <textarea data-testid="notes-input" rows={4} className={`${inputCls} resize-none`} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Bullet points, numbers, learnings you want included…" />
          </div>
        </div>
      )}

      {tab === "url" && (
        <div className="space-y-4 fade-up">
          <div>
            <label className={labelCls}>URL / repo / paper</label>
            <input data-testid="url-input" className={inputCls} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://arxiv.org/abs/… or a blog/repo URL" />
          </div>
          <div>
            <label className={labelCls}>Angle / notes (optional)</label>
            <textarea data-testid="url-notes-input" rows={3} className={`${inputCls} resize-none`} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What angle should the post take?" />
          </div>
        </div>
      )}

      {tab !== "suggest" && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Tone</label>
              <select data-testid="tone-select" className={selectCls} value={tone} onChange={(e) => setTone(e.target.value)}>
                <option value="technical">Technical</option>
                <option value="practical">Practical</option>
                <option value="thought-leadership">Thought-lead</option>
                <option value="casual">Casual</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Length</label>
              <select data-testid="length-select" className={selectCls} value={length} onChange={(e) => setLength(e.target.value)}>
                <option value="short">Short</option>
                <option value="medium">Medium</option>
                <option value="long">Long</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Format</label>
              <select data-testid="format-select" className={selectCls} value={format} onChange={(e) => setFormat(e.target.value)}>
                <option value="post">Post</option>
                <option value="article">Article</option>
              </select>
            </div>
          </div>
          <button data-testid="generate-btn" onClick={generate} disabled={generating}
            className="w-full py-3.5 rounded-full bg-[#0f172a] text-white font-semibold text-sm hover:bg-[#1e293b] transition-colors duration-150 disabled:opacity-50 flex items-center justify-center gap-2">
            {generating ? "Agent drafting…" : "Generate draft"} <ArrowRight size={15} />
          </button>
        </>
      )}
    </div>
  );
}
